import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOnOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import DiscountOutlinedIcon from '@mui/icons-material/DiscountOutlined';
import FlightIcon from '@mui/icons-material/FlightOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import PercentOutlinedIcon from '@mui/icons-material/PercentOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';

const navigationConfig = [
  {
    group: null,
    items: [
      {
        label: 'Dashboard',
        path: '/admin',
        icon: DashboardIcon,
        apiStatus: 'verified',
      },
      {
        label: 'Travel',
        icon: ExploreOutlinedIcon,
        children: [
          {
            /**
             * SMB-4110 — this item carries a second control: a refresh glyph
             * that starts a FRESH conversation rather than just navigating to
             * the chat surface.
             *
             * It exists because clicking "Start Chat" while already on Start
             * Chat did nothing at all. The conversation lives inside the
             * embedded traveller app, and `TravelAppFrame` deliberately skips
             * its `zenvoya:set-surface` message when the frame is already
             * showing the surface being asked for — so the nav item was inert
             * on the one screen an operator most wants to reset from, and
             * there was no explicit way to clear a chat at all.
             *
             * `SidebarNavItem` renders the glyph; `admin/pages/travel/
             * TravelPage.jsx` turns the click into a frame reload.
             */
            newChat: true,
            label: 'Start Chat',
            path: '/admin/travel/chat',
            icon: ChatOutlinedIcon,
          },
          {
            label: 'Book',
            path: '/admin/travel/shop',
            icon: ShoppingBagOutlinedIcon,
          },
          {
            label: 'Chat History',
            path: '/admin/travel/history',
            icon: HistoryOutlinedIcon,
          },
        ],
      },
      {
        label: 'Bookings',
        icon: ConfirmationNumberOutlinedIcon,
        children: [
          {
            label: 'Bookings',
            path: '/admin/bookings',
            icon: ConfirmationNumberOutlinedIcon,
            apiStatus: 'verified',
          },
          {
            label: 'Update Hotel',
            path: '/admin/bookings/manual-hotel',
            icon: PostAddOutlinedIcon,
            apiStatus: 'verified',
          },
          {
            label: 'Update Flight',
            path: '/admin/bookings/manual-flight',
            icon: FlightIcon,
            apiStatus: 'verified',
          },
          {
            label: 'Requests',
            path: '/admin/servicing',
            icon: AssignmentOutlinedIcon,
            // Count of partners' flight requests not opened yet (SidebarNavItem).
            newRequestsCount: true,
          },
        ],
      },
      {
        label: 'Customers',
        path: '/admin/customers',
        icon: PersonOutlinedIcon,
        apiStatus: 'verified',
      },
      {
        label: 'Billing',
        path: '/admin/finance',
        icon: AccountBalanceWalletOutlinedIcon,
        apiStatus: 'verified',
      },
      {
        label: 'Communications',
        path: '/admin/communications',
        icon: ForumOutlinedIcon,
        apiStatus: 'verified',
      },
      {
        label: 'Reports',
        path: '/admin/reports',
        icon: AssessmentOutlinedIcon,
        apiStatus: 'in_progress',
      },
    ],
  },
  {
    group: 'Settings',
    collapsible: true,
    icon: SettingsOutlinedIcon,
    items: [
      {
        label: 'Partners',
        path: '/admin/tenants',
        icon: BusinessOutlinedIcon,
        // Shows a status dot next to this item (green = verified working,
        // yellow = in progress), gated by SHOW_API_STATUS_BADGE in
        // flagSwitch/SwitchConfig — see SidebarNavItem.jsx. QA-only.
        apiStatus: 'verified',
      },
      {
        label: 'Branding',
        path: '/admin/branding',
        icon: PaletteOutlinedIcon,
        apiStatus: 'verified',
      },
      {
        label: 'Revenue',
        icon: FlightIcon,
        children: [
          {
            label: 'Service Fees',
            path: '/admin/service-fees',
            icon: MonetizationOnIcon,
            apiStatus: 'verified',
          },
          {
            label: 'Markups',
            path: '/admin/markup-rules',
            icon: TrendingUpIcon,
            apiStatus: 'verified',
          },
          {
            label: 'Deal Codes',
            path: '/admin/deal-codes',
            icon: LocalOfferOutlinedIcon,
            apiStatus: 'in_progress',
          },
          {
            label: 'Dropnet',
            path: '/admin/dropnet',
            icon: DiscountOutlinedIcon,
            apiStatus: 'verified',
          },
          {
            label: 'Discount',
            path: '/admin/discounts',
            icon: PercentOutlinedIcon,
            apiStatus: 'in_progress',
          },
        ],
      },
      {
        label: 'GST',
        path: '/admin/gst',
        icon: PercentOutlinedIcon,
        // Live since 2026-10-08 (registry.json gstRatesView / gstRatesUpdate
        // are live:true). Flip to 'verified' once smoke-tested on the stage.
        apiStatus: 'in_progress',
      },
      {
        label: 'Suppliers',
        icon: LocalShippingIcon,
        children: [
          {
            label: 'Suppliers',
            path: '/admin/suppliers',
            icon: LocalShippingIcon,
            apiStatus: 'verified',
          },
          {
            label: 'Partner Mapping',
            path: '/admin/supplier-mapping',
            icon: AccountTreeOutlinedIcon,
            apiStatus: 'verified',
          },
          {
            // localStorage only until the backend route exists (Api/airlinesConfigAPI).
            label: 'Airlines Config',
            path: '/admin/airlines-config',
            icon: FlightIcon,
            apiStatus: 'in_progress',
          },
        ],
      },
      {
        label: 'Users & Roles',
        path: '/admin/users',
        icon: PeopleOutlinedIcon,
        apiStatus: 'verified',
      },
      {
        label: 'Features',
        path: '/admin/feature-flags',
        icon: FlagOutlinedIcon,
        apiStatus: 'verified',
      },
      // Approval Queue is no longer its own destination — it moved to a tab
      // inside Tenant Management (approval requests ARE tenant-creation
      // requests, so this reads as a view on tenants, not a separate area).
    ],
  },
];

export default navigationConfig;
